import React, { useMemo } from 'react';
import { Optional } from 'utility-types';

import { CustomerType, PerformanceDataV2, PerformanceOrderTypes } from 'apis/merchant-gateway/insights';

import { OrderType } from '../../types';
import VerticalBarChart from '../charts/VerticalBarChart';
import { ChartData, Legends } from '../charts/types';

export type CustomerBaseChartProps = {
  legends: Legends<CustomerType> | Optional<Legends<OrderType>, OrderType.WoltPlus>;
  data: PerformanceDataV2<string, PerformanceOrderTypes>[];
  summaryKey: string;
  hideNewVsReturningInsights: boolean;
};

const transformPerformanceDataToCustomersCount = (
  acc: {
    [CustomerType.NEW]: number;
    [CustomerType.RETURNING]: number;
    [OrderType.Regular]: number;
    [OrderType.WoltPlus]: number;
  },
  item: PerformanceDataV2<PerformanceOrderTypes>,
) => {
  if (!item.total) {
    return acc;
  }

  const customersCount = item.total.customersCount ?? 0;
  const regularCustomersCount = item.label === OrderType.Regular ? customersCount : 0;
  const woltPlusCustomersCount = item.label === OrderType.WoltPlus ? customersCount : 0;
  const firstTimeCustomersCount = item.total.firstTimeCustomersCount ?? 0;
  const returningCustomersCount = item.total.returningCustomersCount ?? 0;

  return {
    [OrderType.Regular]: acc[OrderType.Regular] + regularCustomersCount,
    [OrderType.WoltPlus]: acc[OrderType.WoltPlus] + woltPlusCustomersCount,
    [CustomerType.NEW]: acc[CustomerType.NEW] + firstTimeCustomersCount,
    [CustomerType.RETURNING]: acc[CustomerType.RETURNING] + returningCustomersCount,
  };
};

const CustomerBaseChart = ({ legends, data, summaryKey }: CustomerBaseChartProps) => {
  const chartData: ChartData<Legends<CustomerType | OrderType>> = useMemo(
    () =>
      data.map(({ label, split, total }) => {
        const ordersCount = (split || []).reduce(transformPerformanceDataToCustomersCount, {
          [CustomerType.NEW]: 0,
          [CustomerType.RETURNING]: 0,
          [OrderType.Regular]: 0,
          [OrderType.WoltPlus]: 0,
        });

        return {
          ...ordersCount,
          summaryValue: total?.customersCount ?? 0,
          name: label || '',
        };
      }),
    [data],
  );

  return <VerticalBarChart legends={legends} isStacked data={chartData} summaryKey={summaryKey} />;
};

export default CustomerBaseChart;
